import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getScopeLabel } from "./shared/scopeLabels";

// ---------- Brand palette (mirrors src/components/dashboard-v2/shared/styles.js) ----------
const BRAND_ARGB = "FF004A53";
const BLUE_ARGB = "FF005654";
const BLUE_TINT_ARGB = "FFE8F3F2";
const INTERACTION_ARGB = "FF026F6D";
const IRIS_ARGB = "FF5944C6";
const IRIS_TINT_ARGB = "FFF1EFFB";
const AZURE_ARGB = "FF1781E3";
const AZURE_TINT_ARGB = "FFE9F3FD";
const TURQUOISE_ARGB = "FF0EABA9";
const TURQUOISE_TINT_ARGB = "FFE8F8F7";
const GOOD_ARGB = "FF15803D";
const CRITICAL_ARGB = "FFB91C1C";
const GREY_ARGB = "FF6B7280";
const ROW_EVEN_ARGB = "FFF8F9FB";
const BORDER_ARGB = "FFDDDDDD";

const SCOPE_FILL_ARGB = { "Scope 1": IRIS_TINT_ARGB, "Scope 2": AZURE_TINT_ARGB, "Scope 3": TURQUOISE_TINT_ARGB };
const SCOPE_ACCENT_ARGB = { "Scope 1": IRIS_ARGB, "Scope 2": AZURE_ARGB, "Scope 3": TURQUOISE_ARGB };

const BRAND_RGB = [0, 74, 83];
const BLUE_RGB = [0, 86, 84];
const INTERACTION_RGB = [2, 111, 109];
const IRIS_RGB = [89, 68, 198];
const AZURE_RGB = [23, 129, 227];
const TURQUOISE_RGB = [14, 171, 169];
const GOOD_RGB = [21, 128, 61];
const COST_ORANGE_RGB = [245, 166, 35];
const GREY_RGB = [107, 114, 128];
const ROW_EVEN_RGB = [248, 249, 251];
const BORDER_RGB = [221, 221, 221];
const TEXT_RGB = [15, 23, 42];
const SURFACE_RGB = [248, 250, 251];
const SCOPE_ACCENT_RGB = { "Scope 1": IRIS_RGB, "Scope 2": AZURE_RGB, "Scope 3": TURQUOISE_RGB };

const THIN_BORDER = { style: "thin", color: { argb: BORDER_ARGB } };
const CELL_BORDER = { top: THIN_BORDER, left: THIN_BORDER, bottom: THIN_BORDER, right: THIN_BORDER };

function aggregateByScope(rows) {
  return (rows || []).reduce((acc, row) => {
    if (!row?.scope || row.total_emissions == null) return acc;
    if (row.scope.includes(".")) return acc;
    acc[row.scope] = (acc[row.scope] || 0) + Number(row.total_emissions || 0);
    return acc;
  }, {});
}

function scopesToShowFor(scopeGroup) {
  if (scopeGroup === "scope1_2") return ["Scope 1", "Scope 2"];
  if (scopeGroup === "scope3") return ["Scope 3"];
  return ["Scope 1", "Scope 2", "Scope 3"];
}

function groupTotal(scopeTotals, scopeGroup) {
  if (scopeGroup === "scope1_2") return (scopeTotals["Scope 1"] || 0) + (scopeTotals["Scope 2"] || 0);
  if (scopeGroup === "scope3") return scopeTotals["Scope 3"] || 0;
  return Object.values(scopeTotals || {}).reduce((sum, v) => sum + v, 0);
}

function subScopeRows(rows, scopesToShow) {
  return (rows || []).reduce((acc, row) => {
    if (!row?.scope || row.total_emissions == null) return acc;
    if (!scopesToShow.some((scope) => row.scope.startsWith(scope + "."))) return acc;
    const existing = acc.find((r) => r.scope === row.scope);
    if (existing) existing.total_emissions += Number(row.total_emissions || 0);
    else acc.push({ scope: row.scope, total_emissions: Number(row.total_emissions || 0) });
    return acc;
  }, []);
}

function buildTrendSeries(years, byYear, scopeGroup) {
  return years
    .map((year) => ({
      year,
      actual: groupTotal(aggregateByScope(byYear[year] || []), scopeGroup)
    }))
    .filter((row) => row.actual > 0)
    .sort((a, b) => a.year - b.year);
}

function scopeGroupLabel(scopeGroup) {
  if (scopeGroup === "scope1_2") return "Scope 1 + 2";
  if (scopeGroup === "scope3") return "Scope 3";
  return "Alle Scopes";
}

function sanitizeCompany(companyName) {
  return (companyName || "").replace(/\s+/g, "_").replace(/[^\w]/g, "");
}

function buildFilename(companyName, currentYear, includeTimestamp, extension) {
  const company = sanitizeCompany(companyName);
  const timestamp = new Date().toISOString().slice(0, 10);
  return includeTimestamp
    ? `GreenOne_Emissionen_${company}_${currentYear}_${timestamp}.${extension}`
    : `GreenOne_Emissionen_${company}_${currentYear}.${extension}`;
}

function measuresRows(dataset) {
  const measures = Array.isArray(dataset.selectedMeasures) ? dataset.selectedMeasures : [];
  return [...measures]
    .sort((a, b) => (a.startYear || 0) - (b.startYear || 0))
    .map((measure) => ({
      name: measure.name || measure.id,
      scope: measure.scope || "–",
      kostenstelle: measure.Kostenstelle || "–",
      description: measure.description || measure.beschreibung || "",
      funding: measure.funding || "–",
      priorityScore: Number.isFinite(Number(measure.priorityScore)) ? Number(measure.priorityScore) : null,
      isRefined: !!measure.isRefined,
      einsparung: Number(measure.einsparung_pro_jahr) || 0,
      kosteneinsparungPct: Number(measure.relative_kosteneinsparung) || 0,
      kostenProJahr: (Number(measure.anteil_gesamtkosten) || 0) * (Number(measure.relative_kosteneinsparung) || 0) * 10,
      investition: Number(measure.investitionsbedarf) || 0,
      amortisation: Number(measure.amortisationszeit) || 0,
      startYear: measure.startYear || "–"
    }));
}

function pathwayKpiRows(dataset, scopeGroup) {
  const series = buildProjectionSeries(dataset.years || [], dataset.byYear || {}, scopeGroup, dataset.selectedMeasures || []);
  if (series.length === 0) return [];

  const firstYearTotal = series[0]?.actual || 0;
  const projection2050 = series.find((row) => row.year === 2050)?.withMeasures
    ?? series.find((row) => row.year === 2050)?.trajectory
    ?? null;
  const reductionPct = projection2050 != null && firstYearTotal > 0
    ? ((firstYearTotal - projection2050) / firstYearTotal) * 100
    : null;
  const measures = Array.isArray(dataset.selectedMeasures) ? dataset.selectedMeasures : [];
  const annualSavings = measures.reduce((sum, measure) => sum + (Number(measure.einsparung_pro_jahr) || 0), 0);
  const totalInvestment = measures.reduce((sum, measure) => sum + (Number(measure.investitionsbedarf) || 0), 0);
  const fundingCount = measures.filter((measure) => measure.funding && measure.funding !== "–" && measure.funding !== "-").length;
  const paybackValues = measures
    .map((measure) => Number(measure.amortisationszeit))
    .filter((value) => Number.isFinite(value) && value > 0);
  const averagePayback = paybackValues.length
    ? paybackValues.reduce((sum, value) => sum + value, 0) / paybackValues.length
    : null;

  return [
    { metric: "CO₂ heute", value: firstYearTotal > 0 ? `${firstYearTotal.toLocaleString("de-CH", { maximumFractionDigits: 1 })} t/a` : "–" },
    { metric: "Projektion 2050", value: projection2050 != null ? `${Number(projection2050).toLocaleString("de-CH", { maximumFractionDigits: 1 })} t/a` : "–", helper: reductionPct != null ? `${reductionPct.toFixed(0)}% ggü. heute` : null },
    { metric: "Jährliche Einsparung", value: annualSavings > 0 ? `${annualSavings.toLocaleString("de-CH", { maximumFractionDigits: 1 })} t/a` : "–", helper: "bei Vollwirkung" },
    { metric: "Investition gesamt", value: totalInvestment > 0 ? `${totalInvestment.toLocaleString("de-CH", { maximumFractionDigits: 0 })} CHF` : "–", helper: averagePayback != null ? `Ø Amortisation ${averagePayback.toFixed(1)} a` : null },
    { metric: "Förderfähig", value: fundingCount > 0 ? `${fundingCount} Massnahmen` : "–", helper: "mit kant./eidg. Programm" }
  ];
}

function summaryRows(dataset, scopeGroup) {
  const total = groupTotal(dataset.scopeTotals || {}, scopeGroup);
  const previousTotal = groupTotal(aggregateByScope(dataset.prevYearRows), scopeGroup);
  const change = previousTotal ? ((total - previousTotal) / previousTotal) * 100 : null;
  const perEmployee = dataset.employees && dataset.employees > 0 ? total / dataset.employees : null;
  const netZeroStatus = dataset.netZero?.onTrack == null
    ? "Basisjahr"
    : dataset.netZero.onTrack
      ? "On Track"
      : "Nicht on Track";

  return [
    { metric: "Gesamtemissionen", value: total > 0 ? `${total.toLocaleString("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} t CO₂e` : null },
    { metric: "Veränderung zum Vorjahr", value: change != null ? `${change > 0 ? "+" : ""}${change.toFixed(1)} %` : null },
    { metric: "Emissionen / Mitarbeiter", value: perEmployee != null ? `${perEmployee.toFixed(2)} t CO₂e` : null },
    { metric: "CO₂-Intensität Energie", value: dataset.co2IntensityOverall != null ? `${Number(dataset.co2IntensityOverall).toFixed(3)} kg CO₂/prod. h` : null },
    { metric: "Datenqualität", value: dataset.dataQualityScore != null ? `${dataset.dataQualityScore}%` : null },
    { metric: "Netto-Null-Kurs", value: dataset.netZero?.progressPct != null ? `${Number(dataset.netZero.progressPct).toFixed(1)} % · ${netZeroStatus}` : null }
  ].filter((row) => row.value != null);
}

function scoringRows(dataset) {
  const rows = [];
  const overall = dataset.co2Intensity?.overall;

  if (overall) {
    rows.push({
      metric: "CO₂-Intensität Energie",
      unit: "kg CO₂/prod. h",
      betrieb: overall.betrieb ?? null,
      branchenschnitt: overall.branchenschnitt ?? null,
      top25: overall.top25 ?? null
    });
  }

  const categories = dataset.co2Intensity?.categories;
  if (Array.isArray(categories)) {
    categories.forEach((category) => {
      rows.push({
        metric: category.label || category.name || category.key || "Kategorie",
        unit: category.unit || "kg CO₂/prod. h",
        betrieb: category.betrieb ?? category.value?.betrieb ?? null,
        branchenschnitt: category.branchenschnitt ?? category.value?.branchenschnitt ?? null,
        top25: category.top25 ?? category.value?.top25 ?? null
      });
    });
  }
  else if (categories && typeof categories === "object") {
    Object.entries(categories).forEach(([key, category]) => {
      rows.push({
        metric: category?.label || category?.name || key,
        unit: category?.unit || "kg CO₂/prod. h",
        betrieb: category?.betrieb ?? category?.value?.betrieb ?? null,
        branchenschnitt: category?.branchenschnitt ?? category?.value?.branchenschnitt ?? null,
        top25: category?.top25 ?? category?.value?.top25 ?? null
      });
    });
  }

  if (dataset.fleetElectrificationPct != null) {
    rows.push({ metric: "Flotten-Elektrifizierung", unit: "%", betrieb: dataset.fleetElectrificationPct, branchenschnitt: null, top25: null });
  }

  if (dataset.waterBasePaintPct != null) {
    rows.push({ metric: "Wasserbasislack-Anteil", unit: "%", betrieb: dataset.waterBasePaintPct, branchenschnitt: null, top25: null });
  }

  if (dataset.repairQuota != null) {
    rows.push({ metric: "Reparaturquote", unit: "%", betrieb: dataset.repairQuota, branchenschnitt: null, top25: null });
  }

  return rows.filter((row) => row.betrieb != null || row.branchenschnitt != null || row.top25 != null);
}

function buildProjectionSeries(years, byYear, scopeGroup, selectedMeasures) {
  const historical = buildTrendSeries(years, byYear, scopeGroup);
  if (historical.length === 0) return [];

  const first = historical[0];
  const last = historical[historical.length - 1];
  const yearsDiff = last.year - first.year;
  const annualChange = yearsDiff > 0 ? (last.actual - first.actual) / yearsDiff : 0;
  const yearMap = new Map(historical.map((row) => [row.year, { year: row.year, actual: row.actual }]));

  for (let year = last.year; year <= 2050; year += 1) {
    const existing = yearMap.get(year) || { year };
    yearMap.set(year, { ...existing, trajectory: Math.max(last.actual + (year - last.year) * annualChange, 0) });
  }

  const rows = Array.from(yearMap.values()).sort((a, b) => a.year - b.year);
  rows.forEach((row) => {
    const base = Number.isFinite(row.actual) ? row.actual : Number.isFinite(row.trajectory) ? row.trajectory : 0;
    let reduction = 0;
    let investCarrySum = 0;
    let steadyReductionPP = 0;

    (selectedMeasures || []).forEach((measure) => {
      const start = Number(measure.startYear);
      if (Number.isNaN(start) || row.year < start) return;
      reduction += Number(measure.einsparung_pro_jahr) || 0;
      const investBoost = Number(measure.relativ_investition) || 0;
      const share = (Number(measure.anteil_gesamtkosten) || 0) / 100;
      const savingPct = (Number(measure.relative_kosteneinsparung) || 0) / 100;
      const yearsSince = row.year - start;
      if (yearsSince === 0) investCarrySum += investBoost;
      if (yearsSince >= 1) steadyReductionPP += share * savingPct * 100;
    });

    row.withMeasures = selectedMeasures?.length ? Math.max(base - reduction, 0) : null;
    row.costIndex = selectedMeasures?.length ? Math.max(100 + investCarrySum - steadyReductionPP, 0) : null;
    row.netZeroPath = first.actual > 0 ? Math.max(first.actual * (1 - (row.year - first.year) / (2050 - first.year)), 0) : null;
  });

  return rows;
}

// ================================================================
// Excel export
// ================================================================

function addTitleBlock(sheet, columnCount, title, subtitle) {
  sheet.mergeCells(1, 1, 1, columnCount);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
  titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BLUE_ARGB } };
  titleCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
  sheet.getRow(1).height = 26;

  sheet.mergeCells(2, 1, 2, columnCount);
  const subtitleCell = sheet.getCell(2, 1);
  subtitleCell.value = subtitle;
  subtitleCell.font = { italic: true, size: 10, color: { argb: GREY_ARGB } };
  subtitleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BLUE_TINT_ARGB } };
  subtitleCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
  sheet.getRow(2).height = 18;
}

function styleHeaderRow(row, columnCount) {
  row.height = 20;
  for (let col = 1; col <= columnCount; col++) {
    const cell = row.getCell(col);
    cell.font = { bold: true, size: 10, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND_ARGB } };
    cell.alignment = { horizontal: col === 1 ? "left" : "center", vertical: "middle" };
    cell.border = CELL_BORDER;
  }
}

function styleDataRow(row, columnCount, { isEven, scopeFillArgb, indent } = {}) {
  for (let col = 1; col <= columnCount; col++) {
    const cell = row.getCell(col);
    cell.border = CELL_BORDER;
    cell.font = { ...(cell.font || {}), size: 10 };
    if (scopeFillArgb) {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: scopeFillArgb } };
    } else if (isEven) {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ROW_EVEN_ARGB } };
    }
    if (col === 1 && indent) {
      cell.alignment = { ...(cell.alignment || {}), indent };
    }
  }
}

export async function exportToExcel(config, dataset) {
  const { sections, scopeGroup, includeTimestamp } = config;
  const { companyName, currentYear, currentYearRows, prevYearRows, years, byYear } = dataset;

  const scopesToShow = scopesToShowFor(scopeGroup);
  const currentAgg = aggregateByScope(currentYearRows);
  const prevAgg = aggregateByScope(prevYearRows);
  const generatedOn = new Date().toLocaleDateString("de-CH");
  const subtitle = `${companyName || "GreenOne"} · ${scopeGroupLabel(scopeGroup)} · Erstellt am ${generatedOn}`;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "GreenOne Dashboard";
  workbook.created = new Date();

  if (sections.summary) {
    const rows = summaryRows(dataset, scopeGroup);
    if (rows.length > 0) {
      const columnCount = 2;
      const sheet0 = workbook.addWorksheet("Zusammenfassung", {
        views: [{ state: "frozen", ySplit: 3 }],
        properties: { tabColor: { argb: BRAND_ARGB } }
      });
      sheet0.columns = [
        { key: "metric", width: 42 },
        { key: "value", width: 30 }
      ];

      addTitleBlock(sheet0, columnCount, "Zusammenfassung", subtitle);

      const header0 = sheet0.getRow(3);
      header0.values = ["Kennzahl", "Wert"];
      styleHeaderRow(header0, columnCount);

      rows.forEach((entry, index) => {
        const row = sheet0.getRow(4 + index);
        row.values = entry;
        styleDataRow(row, columnCount, { isEven: index % 2 === 0 });
        row.getCell(1).font = { bold: true };
        row.getCell(2).alignment = { horizontal: "right" };
      });
    }
  }

  if (sections.emissions) {
    const columnCount = 5;
    const sheet1 = workbook.addWorksheet("Emissions-Performance", {
      views: [{ state: "frozen", ySplit: 3 }],
      properties: { tabColor: { argb: INTERACTION_ARGB } }
    });
    sheet1.columns = [
      { key: "scope", width: 12 },
      { key: "subcategory", width: 47 },
      { key: "now", width: 25, style: { numFmt: "#,##0.00" } },
      { key: "before", width: 25, style: { numFmt: "#,##0.00" } },
      { key: "change", width: 18, style: { numFmt: "+0.0;-0.0;0.0" } }
    ];

    addTitleBlock(sheet1, columnCount, `Emissions-Performance — ${currentYear}`, subtitle);

    const headerRow = sheet1.getRow(3);
    headerRow.values = ["Scope", "Unterkategorie", "Emissionen aktuell (t CO₂e)", "Emissionen Vorjahr (t CO₂e)", "Veränderung (%)"];
    styleHeaderRow(headerRow, columnCount);
    sheet1.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3, column: columnCount } };

    let rowIndex = 4;
    scopesToShow.forEach((scope) => {
      const now = currentAgg[scope] || 0;
      const before = prevAgg[scope] || 0;
      const change = before ? ((now - before) / before) * 100 : null;

      const totalRow = sheet1.getRow(rowIndex++);
      totalRow.values = { scope, subcategory: "Total", now, before, change };
      totalRow.font = { bold: true };
      styleDataRow(totalRow, columnCount, { scopeFillArgb: SCOPE_FILL_ARGB[scope] });
      totalRow.getCell(1).font = { bold: true, color: { argb: SCOPE_ACCENT_ARGB[scope] } };

      subScopeRows(currentYearRows, [scope]).forEach((sub, subIndex) => {
        const prevRow = subScopeRows(prevYearRows, [scope]).find((r) => r.scope === sub.scope);
        const beforeVal = prevRow ? prevRow.total_emissions : 0;
        const changeSub = beforeVal ? ((sub.total_emissions - beforeVal) / beforeVal) * 100 : null;

        const dataRow = sheet1.getRow(rowIndex++);
        dataRow.values = {
          scope: "",
          subcategory: getScopeLabel(sub.scope),
          now: sub.total_emissions,
          before: beforeVal,
          change: changeSub
        };
        styleDataRow(dataRow, columnCount, { isEven: subIndex % 2 === 0, indent: 1 });
      });
    });

    for (let row = 4; row < rowIndex; row++) {
      const changeCell = sheet1.getRow(row).getCell(5);
      const val = parseFloat(changeCell.value);
      if (!isNaN(val) && val !== 0) {
        changeCell.font = { ...(changeCell.font || {}), bold: true, color: { argb: val > 0 ? CRITICAL_ARGB : GOOD_ARGB } };
      }
    }
  }

  if (sections.pathway) {
    const columnCount = 5;
    const sheet2 = workbook.addWorksheet("Verlauf & Projektion", {
      views: [{ state: "frozen", ySplit: 3 }],
      properties: { tabColor: { argb: AZURE_ARGB } }
    });
    sheet2.columns = [
      { key: "year", width: 12 },
      { key: "actual", width: 22, style: { numFmt: "#,##0.00" } },
      { key: "trajectory", width: 24, style: { numFmt: "#,##0.00" } },
      { key: "withMeasures", width: 28, style: { numFmt: "#,##0.00" } },
      { key: "netZeroPath", width: 26, style: { numFmt: "#,##0.00" } }
    ];

    addTitleBlock(sheet2, columnCount, `CO₂-Verlauf & Projektion — ${scopeGroupLabel(scopeGroup)}`, subtitle);

    const header2 = sheet2.getRow(3);
    header2.values = ["Jahr", "Ist-Emissionen (t CO₂e)", "Trend ohne Massnahmen", "Mit gewählten Massnahmen", "Absenkpfad Netto-Null"];
    styleHeaderRow(header2, columnCount);

    const series = buildProjectionSeries(years, byYear, scopeGroup, dataset.selectedMeasures);
    series.forEach((d, index) => {
      const row = sheet2.getRow(4 + index);
      row.values = d;
      styleDataRow(row, columnCount, { isEven: index % 2 === 0 });
      if (d.year === currentYear) {
        row.getCell(1).font = { bold: true, color: { argb: BLUE_ARGB } };
      }
    });
  }

  if (sections.measures && measuresRows(dataset).length > 0) {
    const columnCount = 7;
    const sheet3 = workbook.addWorksheet("Massnahmen-Plan", {
      views: [{ state: "frozen", ySplit: 3 }],
      properties: { tabColor: { argb: GOOD_ARGB } }
    });
    sheet3.columns = [
      { key: "name", width: 32 },
      { key: "scope", width: 12 },
      { key: "kostenstelle", width: 26 },
      { key: "einsparung", width: 20, style: { numFmt: "#,##0.0" } },
      { key: "kosteneinsparungPct", width: 20, style: { numFmt: "0.0" } },
      { key: "investition", width: 20, style: { numFmt: "#,##0" } },
      { key: "startYear", width: 12 }
    ];

    addTitleBlock(sheet3, columnCount, "Massnahmen-Plan", subtitle);

    const header3 = sheet3.getRow(3);
    header3.values = [
      "Massnahme",
      "Scope",
      "Kostenstelle",
      "Einsparung (t CO₂e/Jahr)",
      "Kosteneinsparung (%)",
      "Investition (CHF)",
      "Startjahr"
    ];
    styleHeaderRow(header3, columnCount);

    const rows = measuresRows(dataset);
    let totalSavings = 0;
    let totalInvestment = 0;

    rows.forEach((measure, index) => {
      const row = sheet3.getRow(4 + index);
      row.values = measure;
      styleDataRow(row, columnCount, { isEven: index % 2 === 0, scopeFillArgb: SCOPE_FILL_ARGB[measure.scope] });
      row.getCell(2).font = { color: { argb: SCOPE_ACCENT_ARGB[measure.scope] || GREY_ARGB }, bold: true };
      totalSavings += measure.einsparung;
      totalInvestment += measure.investition;
    });

    const totalRow = sheet3.getRow(4 + rows.length + 1);
    totalRow.values = { name: "Total", einsparung: totalSavings, investition: totalInvestment };
    for (let col = 1; col <= columnCount; col++) {
      const cell = totalRow.getCell(col);
      cell.font = { bold: true };
      cell.border = { top: { style: "medium", color: { argb: BLUE_ARGB } } };
    }
  }

  if (sections.scoring) {
    const rows = scoringRows(dataset);
    if (rows.length > 0) {
      const columnCount = 5;
      const sheet4 = workbook.addWorksheet("Scoring", {
        views: [{ state: "frozen", ySplit: 3 }],
        properties: { tabColor: { argb: TURQUOISE_ARGB } }
      });
      sheet4.columns = [
        { key: "metric", width: 40 },
        { key: "unit", width: 28 },
        { key: "betrieb", width: 18 },
        { key: "branchenschnitt", width: 18 },
        { key: "top25", width: 18 }
      ];

      addTitleBlock(sheet4, columnCount, "Scoring & betriebliche Kennzahlen", subtitle);

      const header4 = sheet4.getRow(3);
      header4.values = ["Kennzahl", "Einheit", "Betrieb", "Durchschnitt", "Top 25%"];
      styleHeaderRow(header4, columnCount);

      rows.forEach((entry, index) => {
        const row = sheet4.getRow(4 + index);
        row.values = entry;
        styleDataRow(row, columnCount, { isEven: index % 2 === 0 });
        row.getCell(1).font = { bold: true };
      });
    }
  }

  if (sections.netZero && dataset.netZero?.progressPct != null) {
    const columnCount = 2;
    const sheet5 = workbook.addWorksheet("Netto-Null-Kurs", {
      views: [{ state: "frozen", ySplit: 3 }],
      properties: { tabColor: { argb: GOOD_ARGB } }
    });
    sheet5.columns = [
      { key: "metric", width: 40 },
      { key: "value", width: 45 }
    ];

    addTitleBlock(sheet5, columnCount, "Netto-Null-Kurs", subtitle);

    const header5 = sheet5.getRow(3);
    header5.values = ["Kennzahl", "Wert"];
    styleHeaderRow(header5, columnCount);

    const rows = [
      { metric: "Reportingjahr", value: dataset.netZero.year ?? "–" },
      { metric: "Fortschritt", value: `${Number(dataset.netZero.progressPct).toFixed(2)}%` },
      { metric: "Netto-Null kompatibel ab", value: dataset.netZero.thresholdPct != null ? `${Number(dataset.netZero.thresholdPct).toFixed(2)}%` : "–" },
      { metric: "Status", value: dataset.netZero.onTrack == null ? "Basisjahr" : dataset.netZero.onTrack ? "On Track" : "Nicht on Track" }
    ];

    rows.forEach((entry, index) => {
      const row = sheet5.getRow(4 + index);
      row.values = entry;
      styleDataRow(row, columnCount, { isEven: index % 2 === 0 });
      row.getCell(1).font = { bold: true };
    });
  }

  if (workbook.worksheets.length === 0) {
    const infoSheet = workbook.addWorksheet("Export");
    infoSheet.addRow(["Keine Bereiche ausgewählt oder keine Daten verfügbar"]);
  }

  const filename = buildFilename(companyName, currentYear, includeTimestamp, "xlsx");
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    filename
  );
}

// ================================================================
// PDF export
// ================================================================

async function loadPdfLogo() {
  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = "/logos/greenone-header.svg";
    });

    const ratio = (image.naturalWidth || image.width || 1) / (image.naturalHeight || image.height || 1);
    const canvas = document.createElement("canvas");
    canvas.width = 1200;
    canvas.height = Math.max(1, Math.round(canvas.width / ratio));
    const context = canvas.getContext("2d");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    return { dataUrl: canvas.toDataURL("image/png"), ratio };
  } catch {
    return null;
  }
}

function drawLogo(doc, logo, x, y, maxWidth, maxHeight) {
  if (!logo?.dataUrl) return;
  let width = maxWidth;
  let height = width / logo.ratio;
  if (height > maxHeight) {
    height = maxHeight;
    width = height * logo.ratio;
  }
  doc.addImage(logo.dataUrl, "PNG", x, y, width, height);
}

function mixRgb(start, end, t) {
  return start.map((value, index) => Math.round(value + (end[index] - value) * t));
}

function drawGradientRect(doc, x, y, width, height, startRgb, endRgb, steps = 32) {
  const sliceWidth = width / steps;
  for (let i = 0; i < steps; i++) {
    const color = mixRgb(startRgb, endRgb, steps === 1 ? 0 : i / (steps - 1));
    doc.setFillColor(...color);
    doc.rect(x + i * sliceWidth, y, sliceWidth + 0.3, height, "F");
  }
}

function pdfSafeText(value) {
  return String(value ?? "")
    .replaceAll("CO₂e", "CO2e")
    .replaceAll("CO₂", "CO2")
    .replaceAll("–", "-")
    .replaceAll("—", "-")
    .replaceAll("≤", "<=")
    .replaceAll("Ø", "Durchschn.")
    .replaceAll("·", "|");
}

function drawCoverBand(doc, { logo, companyName, currentYear, operationType, scopeGroup, timestamp }) {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  drawGradientRect(doc, 0, 0, pageWidth, 34, BRAND_RGB, INTERACTION_RGB);

  doc.setFillColor(255, 255, 255);
  doc.roundedRect(14, 8, 56, 18, 3, 3, "F");
  drawLogo(doc, logo, 18, 11, 48, 12);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255);
  doc.text("GREENONE REPORTING", pageWidth - 14, 15, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text(`Bericht | ${timestamp}`, pageWidth - 14, 22, { align: "right" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.setTextColor(...BRAND_RGB);
  doc.text("Nachhaltigkeitsbericht", 20, 58, { maxWidth: 170 });

  doc.setFontSize(17);
  doc.setTextColor(...INTERACTION_RGB);
  doc.text(pdfSafeText(currentYear ?? "-"), 20, 71);

  drawGradientRect(doc, 20, 79, 52, 1.6, INTERACTION_RGB, TURQUOISE_RGB, 20);

  const metadata = [
    ["Unternehmen", companyName || "GreenOne"],
    ["Berichtszeitraum", currentYear != null ? String(currentYear) : "–"],
    ["Berichtsumfang", scopeGroupLabel(scopeGroup)],
    ["Betriebstyp", operationType || "–"],
    ["Erstellungsdatum", timestamp]
  ];

  const metadataPositions = [
    [20, 101, 80],
    [110, 101, 80],
    [20, 127, 80],
    [110, 127, 80],
    [20, 153, 170]
  ];

  metadata.forEach(([label, value], index) => {
    const [x, y, width] = metadataPositions[index];
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...GREY_RGB);
    doc.text(label.toUpperCase(), x, y);
    doc.setFontSize(10.5);
    doc.setTextColor(...TEXT_RGB);
    doc.text(pdfSafeText(value), x, y + 8, { maxWidth: width });
    doc.setDrawColor(...BORDER_RGB);
    doc.setLineWidth(0.15);
    doc.line(x, y + 13, x + width, y + 13);
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...INTERACTION_RGB);
  doc.text("ÜBER DIESEN BERICHT", 20, 184);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...TEXT_RGB);
  doc.text(
    "Nachhaltigkeitskennzahlen, Emissionsentwicklung und Massnahmen zur Dekarbonisierung des Betriebs.",
    20,
    194,
    { maxWidth: 118 }
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(60);
  doc.setTextColor(234, 248, 247);
  doc.text(String(currentYear ?? ""), pageWidth - 18, 222, { align: "right" });

  const chapterLabels = ["EMISSIONEN", "ENTWICKLUNG", "MASSNAHMEN"];
  const chapterX = [20, 82, 153];
  chapterLabels.forEach((label, index) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...GREY_RGB);
    doc.text(label, chapterX[index], 239);
  });

  drawGradientRect(doc, 20, 245, pageWidth - 40, 1, BRAND_RGB, TURQUOISE_RGB, 36);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...GREY_RGB);
  doc.text(`Berichtsjahr ${pdfSafeText(currentYear ?? "-")}`, 20, 257);
  doc.text("Alle relevanten Dashboard-Kennzahlen", pageWidth / 2, 257, { align: "center" });
  doc.text("GreenOne", pageWidth - 20, 257, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GREY_RGB);
  doc.text("Erstellt mit GreenOne", 20, pageHeight - 18);
  drawGradientRect(doc, 20, pageHeight - 13, pageWidth - 40, 1.2, BRAND_RGB, TURQUOISE_RGB, 40);
}

function drawSectionHeading(doc, text, y, accentRgb, subtitle) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...accentRgb);
  doc.text("GREENONE REPORTING", 14, y - 7);

  doc.setFontSize(15);
  doc.setTextColor(...BRAND_RGB);
  doc.text(pdfSafeText(text), 14, y);

  drawGradientRect(doc, 14, y + 2.5, 38, 1.1, accentRgb, TURQUOISE_RGB, 16);

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...GREY_RGB);
    doc.text(pdfSafeText(subtitle), 14, y + 9, { maxWidth: 182 });
  }
  doc.setFont("helvetica", "normal");
}

function drawKpiCards(doc, rows, startY) {
  rows.forEach((row, index) => {
    const col = index % 2;
    const line = Math.floor(index / 2);
    const x = 14 + col * 94;
    const y = startY + line * 28;

    doc.setFillColor(252, 253, 253);
    doc.setDrawColor(...BORDER_RGB);
    doc.roundedRect(x, y, 88, 23, 2.5, 2.5, "FD");
    drawGradientRect(doc, x, y, 88, 1.4, BRAND_RGB, INTERACTION_RGB, 24);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...GREY_RGB);
    doc.text(pdfSafeText(row.metric), x + 5, y + 7, { maxWidth: 78 });
    doc.setFontSize(11.5);
    doc.setTextColor(...BRAND_RGB);
    doc.text(pdfSafeText(row.value), x + 5, y + 17, { maxWidth: 78 });
  });
  return startY + Math.ceil(rows.length / 2) * 28;
}

function drawCompactKpiCards(doc, rows, startY) {
  const gap = 3;
  const cardWidth = (182 - gap * 4) / 5;
  const cardHeight = 27;

  rows.slice(0, 5).forEach((row, index) => {
    const x = 14 + index * (cardWidth + gap);
    doc.setFillColor(252, 253, 253);
    doc.setDrawColor(...BORDER_RGB);
    doc.roundedRect(x, startY, cardWidth, cardHeight, 2.2, 2.2, "FD");
    drawGradientRect(doc, x, startY, cardWidth, 1.2, BRAND_RGB, INTERACTION_RGB, 12);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.1);
    doc.setTextColor(...GREY_RGB);
    doc.text(pdfSafeText(row.metric), x + 3, startY + 6.2, { maxWidth: cardWidth - 6 });

    doc.setFontSize(8.8);
    doc.setTextColor(...BRAND_RGB);
    doc.text(pdfSafeText(row.value), x + 3, startY + 15.5, { maxWidth: cardWidth - 6 });

    if (row.helper) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.6);
      doc.setTextColor(...GREY_RGB);
      doc.text(pdfSafeText(row.helper), x + 3, startY + 22.3, { maxWidth: cardWidth - 6 });
    }
  });

  return startY + cardHeight;
}

function drawDonutChart(doc, items, { x, y, size = 46, title, centerLabel, centerValue } = {}) {
  const visibleItems = (items || [])
    .map((item) => ({ ...item, value: Number(item.value || 0) }))
    .filter((item) => item.value > 0);
  const total = visibleItems.reduce((sum, item) => sum + item.value, 0);
  if (total <= 0) return y;

  if (title) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND_RGB);
    doc.text(pdfSafeText(title), x, y);
  }

  const chartY = title ? y + 6 : y;
  const canvas = document.createElement("canvas");
  canvas.width = 600;
  canvas.height = 600;
  const context = canvas.getContext("2d");
  const center = 300;
  const radius = 210;
  let startAngle = -Math.PI / 2;

  context.lineWidth = 120;
  context.lineCap = "butt";

  visibleItems.forEach((item) => {
    const endAngle = startAngle + (item.value / total) * Math.PI * 2;
    context.beginPath();
    context.strokeStyle = `rgb(${item.color.join(",")})`;
    context.arc(center, center, radius, startAngle, endAngle);
    context.stroke();
    startAngle = endAngle;
  });

  doc.addImage(canvas.toDataURL("image/png"), "PNG", x, chartY, size, size);

  if (centerValue) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND_RGB);
    doc.text(pdfSafeText(centerValue), x + size / 2, chartY + size / 2 - 1, { align: "center", maxWidth: size * 0.55 });
  }
  if (centerLabel) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(...GREY_RGB);
    doc.text(pdfSafeText(centerLabel), x + size / 2, chartY + size / 2 + 5, { align: "center" });
  }

  const legendX = x + size + 10;
  let legendY = chartY + 7;
  visibleItems.forEach((item) => {
    doc.setFillColor(...item.color);
    doc.circle(legendX + 1.5, legendY - 1.2, 1.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...TEXT_RGB);
    doc.text(pdfSafeText(item.label), legendX + 6, legendY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...GREY_RGB);
    const percentage = `${((item.value / total) * 100).toFixed(1)}%`;
    doc.text(pdfSafeText(`${item.displayValue || item.value.toLocaleString("de-CH")} | ${percentage}`), legendX + 6, legendY + 5);
    legendY += 14;
  });

  return Math.max(chartY + size, legendY) + 4;
}

function drawSummaryInsights(doc, items, startY) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND_RGB);
  doc.text("Bericht im Kontext", 14, startY);

  const gap = 4;
  const cardWidth = (182 - gap * 2) / 3;
  const cardY = startY + 6;
  const cardHeight = 31;

  items.slice(0, 3).forEach((item, index) => {
    const x = 14 + index * (cardWidth + gap);

    doc.setFillColor(...SURFACE_RGB);
    doc.setDrawColor(...BORDER_RGB);
    doc.roundedRect(x, cardY, cardWidth, cardHeight, 2.5, 2.5, "FD");

    doc.setFillColor(...(item.accent || INTERACTION_RGB));
    doc.circle(x + 5, cardY + 6, 1.4, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.2);
    doc.setTextColor(...GREY_RGB);
    doc.text(pdfSafeText(item.label).toUpperCase(), x + 9, cardY + 7.3, { maxWidth: cardWidth - 12 });

    doc.setFontSize(10.5);
    doc.setTextColor(...BRAND_RGB);
    doc.text(pdfSafeText(item.value), x + 5, cardY + 18, { maxWidth: cardWidth - 10 });

    if (item.helper) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.setTextColor(...GREY_RGB);
      doc.text(pdfSafeText(item.helper), x + 5, cardY + 26, { maxWidth: cardWidth - 10 });
    }
  });

  return cardY + cardHeight;
}

function drawTextWithCo2Subscript(doc, text, x, y, { align = "left" } = {}) {
  const safeText = pdfSafeText(text);
  const parts = safeText.split(/(CO2e?)/g).filter(Boolean);
  const baseFontSize = doc.getFontSize();
  const subscriptFontSize = baseFontSize * 0.68;

  const segments = [];

  parts.forEach((part) => {
    const match = part.match(/^CO2(e?)$/);

    if (match) {
      segments.push({
        text: "CO",
        size: baseFontSize,
        yOffset: 0
      });

      segments.push({
        text: "2",
        size: subscriptFontSize,
        yOffset: 0.9
      });

      if (match[1]) {
        segments.push({
          text: "e",
          size: baseFontSize,
          yOffset: 0
        });
      }
    } else {
      segments.push({
        text: part,
        size: baseFontSize,
        yOffset: 0
      });
    }
  });

  const widths = segments.map((segment) => {
    doc.setFontSize(segment.size);
    return doc.getTextWidth(segment.text);
  });

  const totalWidth = widths.reduce(
    (sum, width) => sum + width,
    0
  );

  let currentX = x;

  if (align === "right") {
    currentX -= totalWidth;
  }

  if (align === "center") {
    currentX -= totalWidth / 2;
  }

  segments.forEach((segment, index) => {
    doc.setFontSize(segment.size);

    doc.text(
      segment.text,
      currentX,
      y + segment.yOffset
    );

    currentX += widths[index];
  });

  doc.setFontSize(baseFontSize);
}

function drawScopeBars(doc, scopeTotals, scopesToShow, startY) {
  const values = scopesToShow.map((scope) => ({
    scope,
    value: Number(scopeTotals?.[scope] || 0)
  }));

  const total = values.reduce(
    (sum, item) => sum + item.value,
    0
  );

  const maxValue = Math.max(
    ...values.map((item) => item.value),
    1
  );

  let y = startY;

  values.forEach(({ scope, value }) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...TEXT_RGB);

    doc.text(
      pdfSafeText(getScopeLabel(scope)),
      14,
      y
    );

    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GREY_RGB);

    drawTextWithCo2Subscript(
      doc,
      `${value.toLocaleString("de-CH", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })} t CO2e`,
      196,
      y,
      { align: "right" }
    );

    doc.setFillColor(237, 241, 245);

    doc.roundedRect(
      14,
      y + 3,
      182,
      5,
      2,
      2,
      "F"
    );

    if (value > 0) {
      doc.setFillColor(
        ...(SCOPE_ACCENT_RGB[scope] || BLUE_RGB)
      );

      doc.roundedRect(
        14,
        y + 3,
        182 * (value / maxValue),
        5,
        2,
        2,
        "F"
      );
    }

    if (total > 0) {
      doc.setFontSize(7);
      doc.setTextColor(...GREY_RGB);

      doc.text(
        `${((value / total) * 100).toFixed(1)}%`,
        196,
        y + 13,
        { align: "right" }
      );
    }

    y += 19;
  });

  return y;
}

function drawLineChart(doc, rows, startY, { targetMax = null } = {}) {
  if (rows.length < 2) return startY;
  const chartX = 22;
  const chartY = startY;
  const chartWidth = 168;
  const chartHeight = 58;
  const values = rows.flatMap((row) => [row.actual, row.trajectory, row.withMeasures, row.netZeroPath].filter((value) => Number.isFinite(Number(value))));
  const maxValue = Math.max(...values.map(Number), 1);
  const firstYear = rows[0].year;
  const lastYear = rows[rows.length - 1].year;
  const yearSpan = Math.max(lastYear - firstYear, 1);
  const xForYear = (year) => chartX + ((year - firstYear) / yearSpan) * chartWidth;
  const yForValue = (value) => chartY + chartHeight - (Number(value) / maxValue) * chartHeight;

  if (targetMax != null && targetMax > 0) {
    const targetHeight = Math.min(chartHeight, (Number(targetMax) / maxValue) * chartHeight);
    doc.setFillColor(236, 253, 243);
    doc.rect(chartX, chartY + chartHeight - targetHeight, chartWidth, targetHeight, "F");
  }

  doc.setDrawColor(...BORDER_RGB);
  doc.setLineWidth(0.15);
  for (let i = 0; i <= 4; i++) {
    const y = chartY + (chartHeight / 4) * i;
    doc.line(chartX, y, chartX + chartWidth, y);
  }

  const drawSeries = (key, color, dash = []) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(0.7);
    doc.setLineDashPattern(dash, 0);
    let previous = null;
    rows.forEach((row) => {
      if (!Number.isFinite(Number(row[key]))) {
        previous = null;
        return;
      }
      const point = { x: xForYear(row.year), y: yForValue(row[key]) };
      if (previous) doc.line(previous.x, previous.y, point.x, point.y);
      previous = point;
    });
  };

  drawSeries("trajectory", GREY_RGB, [2, 2]);
  drawSeries("netZeroPath", GOOD_RGB, [1, 2]);
  drawSeries("withMeasures", INTERACTION_RGB, [3, 2]);
  drawSeries("actual", TURQUOISE_RGB);
  doc.setLineDashPattern([], 0);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...GREY_RGB);
  doc.text(`${maxValue.toLocaleString("de-CH", { maximumFractionDigits: 0 })} t`, chartX - 2, chartY + 2, { align: "right" });
  doc.text("0 t", chartX - 2, chartY + chartHeight, { align: "right" });
  doc.text(String(firstYear), chartX, chartY + chartHeight + 7);
  doc.text(String(lastYear), chartX + chartWidth, chartY + chartHeight + 7, { align: "right" });

  const legendY = chartY + chartHeight + 13;
  const legendItems = [
    ["Ist-Emissionen", TURQUOISE_RGB],
    ["Trend", GREY_RGB],
    ["Netto-Null", GOOD_RGB],
    ["Mit Massnahmen", INTERACTION_RGB]
  ];
  let legendX = chartX;
  legendItems.forEach(([label, color]) => {
    doc.setFillColor(...color);
    doc.circle(legendX + 1.2, legendY - 1.1, 1.2, "F");
    doc.setTextColor(...GREY_RGB);
    doc.setFontSize(6.1);
    doc.text(label, legendX + 4, legendY);
    legendX += doc.getTextWidth(label) + 13;
  });

  return legendY + 6;
}

function drawCostIndexChart(doc, rows, startY) {
  const points = (rows || []).filter((row) => Number.isFinite(Number(row.costIndex)));
  if (points.length < 2) return startY;

  const chartX = 22;
  const chartY = startY + 6;
  const chartWidth = 168;
  const chartHeight = 26;
  const values = points.map((row) => Number(row.costIndex));
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const padding = Math.max((maxValue - minValue) * 0.12, 10);
  const axisMin = Math.max(0, minValue - padding);
  const axisMax = maxValue + padding;
  const span = Math.max(axisMax - axisMin, 1);
  const firstYear = points[0].year;
  const lastYear = points[points.length - 1].year;
  const yearSpan = Math.max(lastYear - firstYear, 1);
  const xForYear = (year) => chartX + ((year - firstYear) / yearSpan) * chartWidth;
  const yForValue = (value) => chartY + chartHeight - ((Number(value) - axisMin) / span) * chartHeight;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND_RGB);
  doc.text("Kostenindex inkl. Investitionsimpuls", 14, startY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...GREY_RGB);
  doc.text("100 = Ausgangsniveau", 87, startY);

  doc.setDrawColor(...BORDER_RGB);
  doc.setLineWidth(0.15);
  for (let i = 0; i <= 2; i++) {
    const y = chartY + (chartHeight / 2) * i;
    doc.line(chartX, y, chartX + chartWidth, y);
  }

  if (axisMin <= 100 && axisMax >= 100) {
    const baselineY = yForValue(100);
    doc.setDrawColor(...GREY_RGB);
    doc.setLineDashPattern([1.5, 1.5], 0);
    doc.line(chartX, baselineY, chartX + chartWidth, baselineY);
    doc.setLineDashPattern([], 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.8);
    doc.setTextColor(...GREY_RGB);
    doc.text("Basis 100", chartX + chartWidth - 1, baselineY - 1.5, { align: "right" });
  }

  doc.setDrawColor(...COST_ORANGE_RGB);
  doc.setLineWidth(0.8);
  doc.setLineDashPattern([3, 1.5], 0);
  let previous = null;
  points.forEach((row) => {
    const point = { x: xForYear(row.year), y: yForValue(row.costIndex) };
    if (previous) doc.line(previous.x, previous.y, point.x, point.y);
    previous = point;
  });
  doc.setLineDashPattern([], 0);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.2);
  doc.setTextColor(...GREY_RGB);
  doc.text(`${axisMax.toFixed(0)}%`, chartX - 2, chartY + 2, { align: "right" });
  doc.text(`${axisMin.toFixed(0)}%`, chartX - 2, chartY + chartHeight, { align: "right" });
  doc.text(String(firstYear), chartX, chartY + chartHeight + 6);
  doc.text(String(lastYear), chartX + chartWidth, chartY + chartHeight + 6, { align: "right" });

  return chartY + chartHeight + 10;
}

function tableTheme({ accentRgb } = {}) {
  return {
    styles: { fontSize: 8, cellPadding: 2.6, textColor: TEXT_RGB, lineColor: [232, 236, 238], lineWidth: 0.08, overflow: "linebreak" },
    headStyles: { fillColor: accentRgb || BLUE_RGB, textColor: [255, 255, 255], fontStyle: "bold", halign: "left", cellPadding: 2.8 },
    alternateRowStyles: { fillColor: ROW_EVEN_RGB },
    margin: { left: 14, right: 14, top: 26, bottom: 18 },
    rowPageBreak: "avoid",
    showHead: "everyPage"
  };
}

export async function exportToPDF(config, dataset) {
  const { sections, scopeGroup, includeTimestamp } = config;
  const { companyName, currentYear, currentYearRows, prevYearRows, years, byYear } = dataset;

  const scopesToShow = scopesToShowFor(scopeGroup);
  const currentAgg = aggregateByScope(currentYearRows);
  const prevAgg = aggregateByScope(prevYearRows);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const filename = buildFilename(companyName, currentYear, includeTimestamp, "pdf");
  const timestamp = new Date().toLocaleDateString("de-CH");
  const logo = await loadPdfLogo();

  drawCoverBand(doc, {
    logo,
    companyName,
    currentYear,
    operationType: dataset.operationType,
    scopeGroup,
    timestamp
  });

  if (sections.summary) {
    const rows = summaryRows(dataset, scopeGroup).map((row) => ({
      ...row,
      metric: pdfSafeText(row.metric),
      value: pdfSafeText(row.value)
    }));

    if (rows.length > 0) {
      doc.addPage();
      drawSectionHeading(doc, "Zusammenfassung", 31, BRAND_RGB, "Die wichtigsten Ergebnisse des Berichts auf einen Blick.");
      let currentY = drawKpiCards(doc, rows, 43);

      if (groupTotal(currentAgg, scopeGroup) > 0) {
        const total = groupTotal(currentAgg, scopeGroup);
        const donutItems = scopesToShow.map((scope) => {
          const value = Number(currentAgg[scope] || 0);
          return {
            label: getScopeLabel(scope),
            value,
            color: SCOPE_ACCENT_RGB[scope] || BLUE_RGB,
            displayValue: `${value.toLocaleString("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} t CO2e`
          };
        });

        currentY = drawDonutChart(doc, donutItems, {
          x: 14,
          y: currentY + 3,
          size: 48,
          title: "Emissionsverteilung",
          centerLabel: "Gesamt",
          centerValue: `${total.toLocaleString("de-CH", { maximumFractionDigits: 1 })} t`
        });

        const previousTotal = groupTotal(prevAgg, scopeGroup);
        const previousChange = previousTotal > 0 ? ((total - previousTotal) / previousTotal) * 100 : null;
        const largestScope = scopesToShow
          .map((scope) => ({ scope, value: Number(currentAgg[scope] || 0) }))
          .sort((a, b) => b.value - a.value)[0];
        const largestShare = largestScope && total > 0 ? (largestScope.value / total) * 100 : null;
        const annualSavings = (dataset.selectedMeasures || [])
          .reduce((sum, measure) => sum + (Number(measure.einsparung_pro_jahr) || 0), 0);

        drawSummaryInsights(doc, [
          {
            label: "Vorjahr",
            value: previousTotal > 0
              ? `${previousTotal.toLocaleString("de-CH", { maximumFractionDigits: 1 })} t`
              : "Keine Vergleichsdaten",
            helper: previousChange != null
              ? `${previousChange > 0 ? "+" : ""}${previousChange.toFixed(1)}% Veränderung`
              : "Kein belastbarer Vergleich",
            accent: AZURE_RGB
          },
          {
            label: "Grösster Scope",
            value: largestScope?.scope || "-",
            helper: largestShare != null ? `${largestShare.toFixed(1)}% der Emissionen` : "Keine Emissionsdaten",
            accent: largestScope ? (SCOPE_ACCENT_RGB[largestScope.scope] || INTERACTION_RGB) : INTERACTION_RGB
          },
          {
            label: "Massnahmenpotenzial",
            value: annualSavings > 0
              ? `${annualSavings.toLocaleString("de-CH", { maximumFractionDigits: 1 })} t/a`
              : "Keine Auswahl",
            helper: annualSavings > 0 ? "jährliche CO2-Einsparung" : "keine Massnahmen ausgewählt",
            accent: GOOD_RGB
          }
        ], currentY + 5);
      }
    }
  }

  if (sections.emissions) {
    const rows = [];
    scopesToShow.forEach((scope) => {
      const now = currentAgg[scope] || 0;
      const before = prevAgg[scope] || 0;
      const change = before ? ((now - before) / before) * 100 : null;
      rows.push([
        getScopeLabel(scope),
        "Total",
        now.toLocaleString("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        before.toLocaleString("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        change !== null ? `${change > 0 ? "+" : ""}${change.toFixed(1)} %` : "–"
      ]);

      subScopeRows(currentYearRows, [scope]).forEach((sub) => {
        const prevRow = subScopeRows(prevYearRows, [scope]).find((r) => r.scope === sub.scope);
        const beforeVal = prevRow ? prevRow.total_emissions : 0;
        const changeSub = beforeVal ? ((sub.total_emissions - beforeVal) / beforeVal) * 100 : null;
        rows.push([
          "",
          getScopeLabel(sub.scope),
          sub.total_emissions.toLocaleString("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          beforeVal.toLocaleString("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          changeSub !== null ? `${changeSub > 0 ? "+" : ""}${changeSub.toFixed(1)} %` : "–"
        ]);
      });
    });

    if (rows.some((row) => row[2] !== "0.0" && row[2] !== "0,0")) {
      doc.addPage();
      drawSectionHeading(doc, "Emissions-Performance", 31, INTERACTION_RGB, "Scope-Verteilung der aktuellen Netto-Emissionen und Vergleich zum Vorjahr.");
      const currentY = drawScopeBars(doc, currentAgg, scopesToShow, 45);
      autoTable(doc, {
        ...tableTheme({ accentRgb: BLUE_RGB }),
        startY: currentY + 2,
        head: [["Scope", "Unterkategorie", "Aktuell (t CO2e)", "Vorjahr (t CO2e)", "Veränderung"]],
        body: rows,
        columnStyles: { 0: { cellWidth: 33, fontStyle: "bold" }, 1: { cellWidth: 56 }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } }
      });
    }
  }

  if (sections.pathway) {
    const series = buildProjectionSeries(years, byYear, scopeGroup, dataset.selectedMeasures);
    if (series.length > 0) {
      doc.addPage();
      drawSectionHeading(doc, "CO2-Verlauf & Projektion", 31, AZURE_RGB, "Ist-Werte sowie Investitions- und Energiekosten der nächsten 10 Jahre; KPI-Projektion bis 2050.");

      const kpiRows = pathwayKpiRows(dataset, scopeGroup).map((row) => ({
        ...row,
        metric: String(row.metric).replaceAll("CO₂", "CO2"),
        helper: row.helper ? String(row.helper).replaceAll("Ø", "Durchschn.") : row.helper
      }));
      let currentY = drawCompactKpiCards(doc, kpiRows, 46);

      const firstYearTotal = series[0]?.actual || 0;
      const targetMax = firstYearTotal > 0 ? firstYearTotal * 0.1 : null;
      if (targetMax != null) {
        doc.setFillColor(236, 253, 243);
        doc.setDrawColor(134, 239, 172);
        doc.roundedRect(14, currentY + 5, 58, 8, 4, 4, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(...GOOD_RGB);
        doc.text(`Zielbereich: <= ${targetMax.toFixed(0)} t`, 18, currentY + 10.2);
      }

      const lastActualYear = Math.max(...years);
      const chartRows = series.filter((row) => row.year <= Math.min(lastActualYear + 10, 2050));
      currentY = drawLineChart(doc, chartRows, currentY + 19, {
        targetMax
      });

      if ((dataset.selectedMeasures || []).length > 0) {
        currentY = drawCostIndexChart(doc, chartRows, currentY + 3);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(...GREY_RGB);
        doc.text("Hinweis: Kostenindex berücksichtigt nur Energiekosten (Strom, Heizung, Treibstoff).", 14, currentY);
        currentY += 5;
      }

      const tableRows = series
        .filter((row) => Number.isFinite(row.actual) || row.year === 2050)
        .map((row) => [
          row.year,
          row.actual != null ? Number(row.actual).toFixed(1) : "–",
          row.trajectory != null ? Number(row.trajectory).toFixed(1) : "–",
          row.withMeasures != null ? Number(row.withMeasures).toFixed(1) : "–",
          row.costIndex != null ? `${Number(row.costIndex).toFixed(1)}%` : "–",
          row.netZeroPath != null ? Number(row.netZeroPath).toFixed(1) : "–"
        ]);

      autoTable(doc, {
        ...tableTheme({ accentRgb: AZURE_RGB }),
        startY: currentY + 1,
        head: [["Jahr", "Ist (t)", "Trend (t)", "Mit Massnahmen (t)", "Kostenindex", "Netto-Null (t)"]],
        body: tableRows,
        styles: { ...tableTheme({ accentRgb: AZURE_RGB }).styles, fontSize: 7.4, cellPadding: 2.2 },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 18 }, 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } }
      });
    }
  }

  if (sections.measures) {
    const rows = measuresRows(dataset);
    if (rows.length > 0) {
      doc.addPage();
      drawSectionHeading(doc, "Massnahmen", 31, GOOD_RGB, "Ausgewählte Massnahmen mit CO2-Wirkung, Kosten, Investition, Förderung, Priorität und Umsetzungshorizont.");

      const savingsByScope = ["Scope 1", "Scope 2", "Scope 3"].map((scope) => {
        const value = rows
          .filter((measure) => measure.scope === scope)
          .reduce((sum, measure) => sum + Number(measure.einsparung || 0), 0);
        return {
          label: getScopeLabel(scope),
          value,
          color: SCOPE_ACCENT_RGB[scope] || BLUE_RGB,
          displayValue: `${value.toLocaleString("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} t/a`
        };
      });
      const totalSavings = savingsByScope.reduce((sum, item) => sum + item.value, 0);
      const totalInvestment = rows.reduce((sum, measure) => sum + measure.investition, 0);
      const fundedCount = rows.filter((measure) => measure.funding && measure.funding !== "–" && measure.funding !== "-").length;
      const refinedCount = rows.filter((measure) => measure.isRefined).length;

      const measuresKpis = [
        { metric: "Ausgewählt", value: `${rows.length} Massnahmen` },
        { metric: "CO2-Einsparung", value: `${totalSavings.toLocaleString("de-CH", { maximumFractionDigits: 1 })} t/a` },
        { metric: "Investition", value: `${totalInvestment.toLocaleString("de-CH", { maximumFractionDigits: 0 })} CHF` },
        { metric: "Förderfähig", value: `${fundedCount} Massnahmen` },
        { metric: "Stufe 2", value: `${refinedCount} verfeinert` }
      ];
      let measuresY = drawCompactKpiCards(doc, measuresKpis, 45);
      measuresY = drawDonutChart(doc, savingsByScope, {
        x: 14,
        y: measuresY + 6,
        size: 39,
        title: "CO2-Einsparpotenzial nach Scope",
        centerLabel: "pro Jahr",
        centerValue: `${totalSavings.toLocaleString("de-CH", { maximumFractionDigits: 1 })} t`
      });

      const measureRows = rows.map((measure) => {
        const details = [measure.name];
        if (measure.description) details.push(measure.description);
        if (measure.funding && measure.funding !== "–" && measure.funding !== "-") details.push(`Förderung: ${measure.funding}`);
        else details.push("Keine Förderung");
        if (measure.isRefined) details.push("Stufe 2 verfeinert");
        return [
          details.join("\n"),
          measure.scope,
          measure.einsparung.toLocaleString("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          measure.kostenProJahr > 0 ? `-${measure.kostenProJahr.toLocaleString("de-CH", { maximumFractionDigits: 0 })}` : "–",
          measure.investition.toLocaleString("de-CH", { maximumFractionDigits: 0 }),
          measure.amortisation > 0 ? `${Number(measure.amortisation).toFixed(1)} a` : "sofort",
          measure.priorityScore != null ? String(measure.priorityScore) : "–",
          String(measure.startYear)
        ];
      });

      autoTable(doc, {
        ...tableTheme({ accentRgb: GOOD_RGB }),
        startY: measuresY + 1,
        head: [["Massnahme / Förderung", "Scope", "CO2 t/a", "Kosten/Jahr CHF", "Invest. CHF", "Amort.", "Prio", "Start"]],
        body: measureRows,
        styles: { ...tableTheme({ accentRgb: GOOD_RGB }).styles, fontSize: 6.6, cellPadding: 2.0 },
        columnStyles: {
          0: { cellWidth: 50, fontStyle: "bold" },
          1: { cellWidth: 17 },
          2: { cellWidth: 18, halign: "right" },
          3: { cellWidth: 23, halign: "right" },
          4: { cellWidth: 22, halign: "right" },
          5: { cellWidth: 17, halign: "right" },
          6: { cellWidth: 13, halign: "right" },
          7: { cellWidth: 14, halign: "right" }
        },
        didParseCell({ row, column, cell }) {
          if (row.section === "body" && column.index === 1 && SCOPE_ACCENT_RGB[cell.raw]) {
            cell.styles.textColor = SCOPE_ACCENT_RGB[cell.raw];
            cell.styles.fontStyle = "bold";
          }
        }
      });
    }
  }

  if (sections.scoring) {
    const rows = scoringRows(dataset);
    if (rows.length > 0) {
      doc.addPage();
      drawSectionHeading(doc, "Scoring & betriebliche Kennzahlen", 31, TURQUOISE_RGB, "Vergleich des Betriebs mit Durchschnitt und Top-25%-Benchmark, soweit verfügbar.");

      const overall = rows.find((row) => row.metric === "CO₂-Intensität Energie");
      const scoringKpis = [
        overall ? { metric: "CO2-Intensität", value: `${Number(overall.betrieb).toLocaleString("de-CH", { maximumFractionDigits: 3 })}`, helper: "kg CO2/prod. h" } : null,
        dataset.dataQualityScore != null ? { metric: "Datenqualität", value: `${Number(dataset.dataQualityScore).toFixed(0)}%` } : null,
        dataset.waterBasePaintPct != null ? { metric: "Wasserbasislack", value: `${Number(dataset.waterBasePaintPct).toFixed(0)}%` } : null,
        dataset.fleetElectrificationPct != null ? { metric: "Flotten-Elektrifizierung", value: `${Number(dataset.fleetElectrificationPct).toFixed(0)}%` } : null,
        dataset.repairQuota != null ? { metric: "Reparaturquote", value: `${Number(dataset.repairQuota).toFixed(0)}%` } : { metric: "Reparaturquote", value: "–", helper: "noch nicht verfügbar" }
      ].filter(Boolean);

      const scoringY = drawCompactKpiCards(doc, scoringKpis, 45);

      autoTable(doc, {
        ...tableTheme({ accentRgb: TURQUOISE_RGB }),
        startY: scoringY + 8,
        head: [["Kennzahl", "Einheit", "Betrieb", "Durchschnitt", "Top 25%"]],
        body: rows.map((row) => [
          String(row.metric).replaceAll("CO₂", "CO2"),
          String(row.unit).replaceAll("CO₂", "CO2"),
          row.betrieb != null ? Number(row.betrieb).toLocaleString("de-CH", { maximumFractionDigits: row.unit === "%" ? 0 : 3 }) : "–",
          row.branchenschnitt != null ? Number(row.branchenschnitt).toLocaleString("de-CH", { maximumFractionDigits: row.unit === "%" ? 0 : 3 }) : "–",
          row.top25 != null ? Number(row.top25).toLocaleString("de-CH", { maximumFractionDigits: row.unit === "%" ? 0 : 3 }) : "–"
        ]),
        columnStyles: { 0: { cellWidth: 54, fontStyle: "bold" }, 1: { cellWidth: 42 }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } }
      });
    }
  }

  if (sections.netZero && dataset.netZero?.progressPct != null) {
    doc.addPage();
    drawSectionHeading(doc, "Netto-Null-Kurs", 31, GOOD_RGB, `Fortschritt zur gesetzlich vorgegebenen Netto-Null bis 2050${dataset.netZero.year ? ` (Stand ${dataset.netZero.year})` : ""}.`);

    const netZeroStatusLabel = dataset.netZero.onTrack == null
      ? "Basisjahr"
      : dataset.netZero.onTrack
        ? "On Track"
        : "Nicht on Track";
    const statusFill = dataset.netZero.onTrack == null
      ? [241, 245, 249]
      : dataset.netZero.onTrack
        ? [236, 253, 243]
        : [254, 242, 242];
    const statusTextColor = dataset.netZero.onTrack == null
      ? GREY_RGB
      : dataset.netZero.onTrack
        ? GOOD_RGB
        : [185, 28, 28];

    doc.setFillColor(...statusFill);
    doc.setDrawColor(...statusTextColor);
    doc.roundedRect(158, 25, 38, 10, 5, 5, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.setTextColor(...statusTextColor);
    doc.text(netZeroStatusLabel, 177, 31.5, { align: "center" });

    const rows = [
      { metric: "Fortschritt", value: `${Number(dataset.netZero.progressPct).toFixed(2)} %` },
      { metric: "Kompatibel ab", value: dataset.netZero.thresholdPct != null ? `${Number(dataset.netZero.thresholdPct).toFixed(2)} %` : "-" },
      { metric: "Status", value: netZeroStatusLabel },
      { metric: "Reportingjahr", value: String(dataset.netZero.year ?? "-") }
    ];

    const currentY = drawKpiCards(doc, rows, 43);
    const scaleMax = Math.max(dataset.netZero.scaleMaxPct ?? 100, dataset.netZero.thresholdPct ?? 0, dataset.netZero.progressPct ?? 0, 100);
    const barY = currentY + 12;
    const progressWidth = 170 * (Math.max(0, dataset.netZero.progressPct) / scaleMax);
    const thresholdX = 20 + 170 * (Math.max(0, dataset.netZero.thresholdPct ?? 0) / scaleMax);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND_RGB);
    doc.text("Fortschritt im Zielpfad", 20, barY - 6);

    doc.setFillColor(237, 241, 245);
    doc.roundedRect(20, barY, 170, 8, 4, 4, "F");
    if (dataset.netZero.thresholdPct != null) {
      doc.setFillColor(236, 253, 243);
      doc.roundedRect(thresholdX, barY, Math.max(0, 190 - thresholdX), 8, 4, 4, "F");
    }
    if (progressWidth > 0) {
      doc.setFillColor(...AZURE_RGB);
      doc.roundedRect(20, barY, progressWidth, 8, 4, 4, "F");
    }
    if (dataset.netZero.thresholdPct != null) {
      doc.setDrawColor(...GOOD_RGB);
      doc.setLineWidth(1);
      doc.line(thresholdX, barY - 3, thresholdX, barY + 11);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.8);
    doc.setTextColor(...GREY_RGB);
    doc.text("0%", 20, barY + 17);
    doc.text(`${scaleMax.toFixed(0)}%`, 190, barY + 17, { align: "right" });
    if (dataset.netZero.thresholdPct != null) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...GOOD_RGB);
      doc.text(`Kompatibilitäts-Schwelle ${Number(dataset.netZero.thresholdPct).toFixed(2)}%`, Math.max(52, Math.min(158, thresholdX)), barY + 17, { align: "center" });
    }

    const progressPct = Number(dataset.netZero.progressPct);
    const thresholdPct = Number(dataset.netZero.thresholdPct);
    const delta = Number.isFinite(thresholdPct) ? progressPct - thresholdPct : null;
    const statusText = dataset.netZero.onTrack == null
      ? "Dieses Reportingjahr bildet das Basisjahr für den weiteren Zielpfad."
      : dataset.netZero.onTrack
        ? `Der Betrieb liegt ${delta != null ? `${Math.abs(delta).toFixed(2)} Prozentpunkte ` : ""}über der für dieses Jahr erforderlichen Schwelle.`
        : `Der Betrieb liegt ${delta != null ? `${Math.abs(delta).toFixed(2)} Prozentpunkte ` : ""}unter der für dieses Jahr erforderlichen Schwelle.`;

    const infoY = barY + 29;
    doc.setFillColor(250, 252, 252);
    doc.setDrawColor(...BORDER_RGB);
    doc.roundedRect(20, infoY, 170, 36, 3, 3, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND_RGB);
    doc.text("Einordnung", 26, infoY + 9);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.4);
    doc.setTextColor(...TEXT_RGB);
    doc.text(statusText, 26, infoY + 17, { maxWidth: 158 });

    const notice = dataset.netZero.notice || "Die Schwelle steigt jährlich bis 2050 linear auf 100% und wird pro Reportingjahr neu berechnet.";
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.8);
    doc.setTextColor(...GREY_RGB);
    doc.text(notice, 26, infoY + 28, { maxWidth: 158 });

    const milestoneY = infoY + 50;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND_RGB);
    doc.text("Zielpfad bis 2050", 20, milestoneY);

    const milestones = [
      { label: "Aktuell", value: `${progressPct.toFixed(1)}%`, x: 20 },
      { label: "Schwelle heute", value: Number.isFinite(thresholdPct) ? `${thresholdPct.toFixed(1)}%` : "-", x: 78 },
      { label: "Ziel 2050", value: "100%", x: 136 }
    ];
    milestones.forEach((item) => {
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(...BORDER_RGB);
      doc.roundedRect(item.x, milestoneY + 6, 52, 24, 2.5, 2.5, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.3);
      doc.setTextColor(...GREY_RGB);
      doc.text(item.label.toUpperCase(), item.x + 4, milestoneY + 13);
      doc.setFontSize(11);
      doc.setTextColor(...BRAND_RGB);
      doc.text(item.value, item.x + 4, milestoneY + 23);
    });
  }

  const pageCount = doc.getNumberOfPages();
  for (let i = 2; i <= pageCount; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    drawLogo(doc, logo, 14, 8, 34, 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...GREY_RGB);
    doc.text(`${companyName || "GreenOne"} | Berichtsjahr ${pdfSafeText(currentYear ?? "-")}`, pageWidth - 14, 13, { align: "right" });
    doc.setDrawColor(...BORDER_RGB);
    doc.line(14, 20, pageWidth - 14, 20);
    drawGradientRect(doc, 14, pageHeight - 14, pageWidth - 28, 0.8, BRAND_RGB, TURQUOISE_RGB, 36);
    doc.text("GreenOne Nachhaltigkeitsbericht", 14, pageHeight - 8);
    doc.text(`Seite ${i} / ${pageCount}`, pageWidth - 14, pageHeight - 8, { align: "right" });
  }

  doc.save(filename);
}